import { useEffect, useState, useCallback } from "react";
import { Loader2, Lightbulb, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUSES = [
  ["new", "New"], ["planned", "Planned"], ["in_progress", "In progress"], ["done", "Done"], ["dismissed", "Dismissed"],
];
const CHIP = {
  new: "bg-[#1e3d3a]/8 text-[#1e3d3a]/70",
  planned: "bg-[#c9a961]/20 text-[#8a6d2f]",
  in_progress: "bg-[#3b6ea5]/15 text-[#3b6ea5]",
  done: "bg-[#2f7d5b]/15 text-[#2f7d5b]",
  dismissed: "bg-[#6b6b6b]/12 text-[#6b6b6b]",
};

export default function RecommendationsBoard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [noteDraft, setNoteDraft] = useState({});

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/recommendations"); setRows(data.recommendations || []); }
    catch { toast.error("Could not load recommendations."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/recommendations/${id}`, { status }); setRows((rs) => rs.map((r) => r.id === id ? { ...r, status } : r)); }
    catch { toast.error("Could not update status."); }
  };

  const saveNote = async (id) => {
    const admin_notes = noteDraft[id] ?? "";
    try {
      await api.patch(`/admin/recommendations/${id}`, { admin_notes });
      setRows((rs) => rs.map((r) => r.id === id ? { ...r, admin_notes } : r));
      toast.success("Note saved — the client can see it in their portal.");
    } catch { toast.error("Could not save note."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this recommendation?")) return;
    try { await api.delete(`/admin/recommendations/${id}`); setRows((rs) => rs.filter((r) => r.id !== id)); }
    catch { toast.error("Could not delete."); }
  };

  const open = rows.filter((r) => !["done", "dismissed"].includes(r.status));
  const shown = filter === "open" ? open : rows;
  const counts = { open: open.length, all: rows.length };

  if (loading) return <div className="flex items-center gap-2 text-[#1e3d3a]/60 p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading recommendations…</div>;

  return (
    <div data-testid="recommendations-board">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[#1e3d3a]"><Lightbulb className="h-5 w-5 text-[#c9a961]" /><h2 className="font-head text-xl">Client Recommendations</h2></div>
        <div className="flex gap-1.5">
          {[["open", `Open · ${counts.open}`], ["all", `All · ${counts.all}`]].map(([id, label]) => (
            <button key={id} data-testid={`rec-filter-${id}`} onClick={() => setFilter(id)}
              className={`rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wide border ${filter === id ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/55"}`}>{label}</button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#1e3d3a]/15 p-10 text-center text-[#1e3d3a]/50" data-testid="rec-empty">
          No recommendations {filter === "open" ? "open right now" : "yet"}. When a client submits one from their portal, it shows up here.
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((r) => (
            <div key={r.id} data-testid={`admin-rec-${r.id}`} className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm text-[#1e3d3a]">
                    <Building2 className="h-4 w-4 text-[#c9a961]" />
                    <span className="font-semibold">{r.client_name}</span>
                    {r.business_name && <span className="text-[#1e3d3a]/50">· {r.business_name}</span>}
                  </div>
                  <div className="text-[11px] text-[#1e3d3a]/40 mt-0.5">{r.client_id} · {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${CHIP[r.status] || CHIP.new}`}>{(STATUSES.find(([s]) => s === r.status) || [])[1] || r.status}</span>
                  <button data-testid={`admin-rec-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1 text-[#1e3d3a]/30 hover:text-[#b23a48]"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>

              <p className="mt-3 text-sm text-[#1e3d3a]"><span className="text-[#1e3d3a]/45 uppercase text-[10px] tracking-wide mr-1">Problem</span> {r.problem}</p>
              {r.suggested_fix && <p className="mt-2 text-sm text-[#1e3d3a]/80"><span className="text-[#1e3d3a]/45 uppercase text-[10px] tracking-wide mr-1">Their fix</span> {r.suggested_fix}</p>}

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {STATUSES.map(([s, label]) => (
                  <button key={s} data-testid={`admin-rec-status-${r.id}-${s}`} onClick={() => setStatus(r.id, s)}
                    className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wide border transition-colors ${r.status === s ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/50 hover:text-[#1e3d3a]/80"}`}>{label}</button>
                ))}
              </div>

              <div className="mt-3 flex gap-2">
                <input data-testid={`admin-rec-note-${r.id}`} className="flex-1 rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none"
                  placeholder="Add a note back to the client…" defaultValue={r.admin_notes || ""}
                  onChange={(e) => setNoteDraft((d) => ({ ...d, [r.id]: e.target.value }))} />
                <button data-testid={`admin-rec-note-save-${r.id}`} onClick={() => saveNote(r.id)}
                  className="rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d]">Save</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
